import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import { Field } from '../components/ui/Field';
import { Panel, StatTile } from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Tag from '../components/ui/Tag';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import { CATEGORY, alpha } from '../components/ui/categoryColors';

/* ------------------------------------------------------------------ */
/*  Summary tile: a figure in its colour on a faint tint of it         */
/* ------------------------------------------------------------------ */
const TintTile = ({ hue, label, value, sub }) => (
  <div className="min-w-0 rounded-panel p-4 text-center" style={{ backgroundColor: alpha(CATEGORY[hue].mark, 0.08) }}>
    <div className="text-2xl font-bold tabular-nums" style={{ color: CATEGORY[hue].ink.oklch }}>{value}</div>
    <div className="mt-1 text-[11px] text-on-surface-variant">{label}</div>
    <div className="mt-0.5 text-[10px] text-on-surface-variant tabular-nums">{sub}</div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Modal                                                               */
/* ------------------------------------------------------------------ */
const DateAvailabilityModal = ({ date, onClose }) => {
  const api = useApi();
  const history = useHistory();
  const toast = useToast();
  const [availability, setAvailability] = useState(null);
  const [nearbyEvents, setNearbyEvents] = useState([]);
  const [showPending, setShowPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tourMode, setTourMode] = useState(false);
  const [tourSelected, setTourSelected] = useState([]);
  const [creatingTour, setCreatingTour] = useState(false);
  const [targetDrones, setTargetDrones] = useState('');
  const [leasesExpanded, setLeasesExpanded] = useState(false);

  useEffect(() => {
    if (!date) return;
    // Reset UI state when date changes
    setShowPending(false);
    setTourMode(false);
    setTourSelected([]);
    setTargetDrones('');
    setLeasesExpanded(false);

    const fetchData = async () => {
      setLoading(true);
      try {
        const [avail, events] = await Promise.all([
          api.get(`/events/availability?date=${date}`),
          api.get(`/events?from_date=${dayjs(date).subtract(10, 'day').format('YYYY-MM-DD')}&to_date=${dayjs(date).add(10, 'day').format('YYYY-MM-DD')}`),
        ]);
        setAvailability(avail);
        setNearbyEvents(events || []);
      } catch (err) {
        console.error('Failed to load availability:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [date]);

  const toggleTourEvent = (id) => {
    setTourSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCreateTour = async () => {
    if (tourSelected.length < 2) {
      toast.error('Select at least 2 events for a tour');
      return;
    }
    setCreatingTour(true);
    try {
      const result = await api.post('/tours', { body: { event_ids: tourSelected } });
      toast.success(`${result.name} created`);
      onClose();
      history.push(`/events/tours/${result.id}`);
    } catch (err) {
      toast.error('Failed to create tour');
    } finally {
      setCreatingTour(false);
    }
  };

  if (!date) return null;

  const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  // Enrich nearby events with zone/distance data
  const targetDate = dayjs(date);
  const enriched = nearbyEvents.map(e => {
    const days = dayjs(e.event_date).diff(targetDate, 'day');
    const ls = e.ls_drone_count || 0;
    const pyro = e.pyro_drone_count || 0;
    const buf = e.buffer_percentage || 0;
    const buffTotal = ls + Math.ceil(ls * buf / 100) + pyro + Math.ceil(pyro * buf / 100);
    return { ...e, days, buffTotal };
  });

  // Filter and sort — cancelled events are always hidden; pending is gated by the toggle
  const nonCancelled = enriched.filter(e => e.status !== 'cancelled');
  const filtered = showPending ? nonCancelled : nonCancelled.filter(e => e.status !== 'pending');
  const pendingCount = nonCancelled.filter(e => e.status === 'pending').length;
  const sorted = [...filtered].sort((a, b) => Math.abs(a.days) - Math.abs(b.days));
  const hasProximityEvents = sorted.length > 0 || pendingCount > 0;

  // Title text color by status — used to color-code event rows (status badge removed)
  const titleColors = {
    pending:   CATEGORY.amber.ink.oklch,
    confirmed: CATEGORY.emerald.ink.oklch,
    packing:   CATEGORY.purple.ink.oklch,
    active:    CATEGORY.sky.ink.oklch,
    completed: 'var(--on-surface-variant)',
    cancelled: alpha(CATEGORY.red.ink, 0.6),
  };

  // Split events into same-day, before, and after
  const sameDayEvents = sorted.filter(e => e.days === 0);
  const beforeEvents = sorted.filter(e => e.days < 0).sort((a, b) => b.days - a.days); // closest first
  const afterEvents = sorted.filter(e => e.days > 0).sort((a, b) => a.days - b.days);  // closest first

  // Group before/after by day distance
  const groupByDays = (events, direction) => {
    const groups = [];
    let currentDays = null;
    events.forEach(e => {
      const absDays = Math.abs(e.days);
      if (absDays !== currentDays) {
        groups.push({ label: `${absDays}d ${direction}`, events: [] });
        currentDays = absDays;
      }
      groups[groups.length - 1].events.push(e);
    });
    return groups;
  };
  const beforeGroups = groupByDays(beforeEvents, 'Before');
  const afterGroups = groupByDays(afterEvents, 'After');

  const renderEvent = (e) => {
    const titleColor = titleColors[e.status] || titleColors.pending;
    const canSelect = tourMode && !e.tour_id && !['cancelled', 'completed'].includes(e.status);
    const isSelected = tourSelected.includes(e.id);

    const rowClass = `flex items-center gap-3 text-sm py-1.5 no-underline ${
      tourMode
        ? (canSelect ? 'cursor-pointer rounded-md px-1 -mx-1 transition-colors ' + (isSelected ? '' : 'hover:bg-gray-700/30') : '')
        : 'cursor-pointer rounded-md px-1 -mx-1 transition-colors hover:bg-gray-700/30'
    }`;

    const rowStyle = tourMode && canSelect && isSelected ? { background: alpha(CATEGORY.cyan.mark, 0.15) } : undefined;

    const inner = (
      <>
        <Appear show={tourMode} className="flex flex-shrink-0">
          <input
            type="checkbox"
            checked={isSelected}
            disabled={!canSelect}
            readOnly
            className="rounded flex-shrink-0 pointer-events-none"
            style={{ accentColor: CATEGORY.cyan.mark.oklch }}
          />
        </Appear>
        <span className="font-medium truncate flex-1" style={{ color: titleColor }}>{e.name}</span>
        {e.tour_id && <Tag size="sm" hex={CATEGORY.cyan.ink.hex} className="flex-shrink-0">Tour</Tag>}
        <span className="text-on-surface text-xs tabular-nums flex-shrink-0 w-10 text-right">{e.buffTotal}</span>
      </>
    );

    // Tour mode: row is a toggle (no navigation)
    if (tourMode) {
      return (
        <div
          key={e.id}
          className={rowClass}
          style={rowStyle}
          onClick={canSelect ? () => toggleTourEvent(e.id) : undefined}
          title={`${e.name} \u2014 ${e.status}`}
        >
          {inner}
        </div>
      );
    }

    // Normal mode: row navigates to the event page (cmd/ctrl+click opens in new tab)
    return (
      <Link
        key={e.id}
        to={`/events/${e.id}`}
        onClick={(evt) => {
          // Keep the modal open on modifier-click (user is opening a new tab)
          if (!evt.metaKey && !evt.ctrlKey && evt.button !== 1) {
            onClose();
          }
        }}
        className={rowClass}
        title={`${e.name} \u2014 ${e.status}`}
      >
        {inner}
      </Link>
    );
  };

  const canTour = enriched.filter(e => !e.tour_id && !['cancelled', 'completed'].includes(e.status)).length >= 2;

  const footer = tourMode ? (
    <>
      <button
        onClick={() => { setTourMode(false); setTourSelected([]); }}
        className={buttonClass('quiet', 'sm')}
      >
        Cancel
      </button>
      <button
        onClick={handleCreateTour}
        disabled={tourSelected.length < 2 || creatingTour}
        className={buttonClass('filled', 'sm')}
      >
        {creatingTour ? 'Creating...' : `Create Tour (${tourSelected.length})`}
      </button>
    </>
  ) : (
    <>
      {canTour && (
        <button
          onClick={() => setTourMode(true)}
          className={buttonClass('info', 'sm')}
        >
          Create Tour
        </button>
      )}
      <Link
        to={`/events/new?date=${date}`}
        className={buttonClass('filled', 'sm')}
      >
        + Create Event
      </Link>
    </>
  );

  const renderGroups = (groups, emptyText) => (
    groups.length > 0 ? (
      <div className="space-y-4">
        {groups.map((group, gi) => (
          <div key={gi}>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-on-surface-variant">{group.label}</div>
            {group.events.map(renderEvent)}
          </div>
        ))}
      </div>
    ) : (
      <EmptyState variant="line" dense>{emptyText}</EmptyState>
    )
  );

  return (
    <Dialog open onClose={onClose} title={dateLabel} meta={<span className="text-[11px] font-semibold uppercase tracking-widest">Availability Details</span>} size="xl" footer={footer} dirty={tourMode && tourSelected.length > 0}>
      <div className="space-y-4">
        <FadeIn key={loading ? 'loading' : availability ? 'loaded' : 'empty'} className="space-y-4">
        {loading ? (
          <SkeletonGroup label="Loading availability" className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-24 rounded-panel" />
              <Skeleton className="h-24 rounded-panel" />
              <Skeleton className="h-24 rounded-panel" />
            </div>
            <Skeleton className="h-28 rounded-panel" />
          </SkeletonGroup>
        ) : availability ? (
          <>
            {/* Availability Summary — 3 columns */}
            {(() => {
              const dronesHealthy = availability.availableDrones > 0;
              const batteriesHealthy = availability.availableBatteries > 0;
              const pyroCommitted = availability.committedPyro || 0;
              const standardCommitted = availability.committedStandard != null ? availability.committedStandard : (availability.committedDrones - pyroCommitted);

              return (
                <div className="grid grid-cols-3 gap-3">
                  <TintTile
                    hue={dronesHealthy ? 'green' : 'red'}
                    label="Available Drones"
                    value={availability.availableDrones.toLocaleString()}
                    sub={`${availability.committedDrones.toLocaleString()} committed${availability.totalLeased > 0 ? ` · ${availability.totalLeased.toLocaleString()} leased` : ''}`}
                  />
                  <TintTile
                    hue={batteriesHealthy ? 'green' : 'red'}
                    label="Available Batteries"
                    value={availability.availableBatteries.toLocaleString()}
                    sub={`${availability.committedBatteries.toLocaleString()} of ${availability.totalBatteries.toLocaleString()} committed`}
                  />
                  <TintTile
                    hue="purple"
                    label="Pyro Committed"
                    value={pyroCommitted.toLocaleString()}
                    sub={`${standardCommitted.toLocaleString()} standard`}
                  />
                </div>
              );
            })()}

            {/* Drone Planning — deficit calculator */}
            {(() => {
              const droneTarget = parseInt(targetDrones) || 0;
              const deficit = droneTarget > 0 ? droneTarget - (availability.availableDrones || 0) : 0;
              return (
                <Panel on="panel" title="Drone Planning" titleStyle="section" accent="amber" level={3} padding="sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-on-surface-variant block mb-1">Target Drones Needed</label>
                      <Field
                        type="number"
                        min="0"
                        value={targetDrones}
                        onChange={(e) => setTargetDrones(e.target.value)}
                        placeholder="e.g. 800"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <Appear show={droneTarget > 0} as="div" className="flex-1">
                        <FadeIn key={deficit > 0 ? 'short' : 'surplus'}>
                        {deficit > 0 ? (
                          <StatTile
                            label="Short by"
                            value={`-${deficit.toLocaleString()}`}
                            tone="danger"
                            sub={`Need to build ${deficit.toLocaleString()} more`}
                          />
                        ) : (
                          <StatTile
                            label="Surplus"
                            value={`+${Math.abs(deficit).toLocaleString()}`}
                            tone="good"
                            sub="You're covered"
                          />
                        )}
                        </FadeIn>
                    </Appear>
                  </div>
                </Panel>
              );
            })()}

            {/* Leases Active on This Date — collapsed by default, click header to expand */}
            {availability.leases && availability.leases.length > 0 && (
              <Panel on="panel" label="Leased out" padding="sm">
                <button
                  type="button"
                  onClick={() => setLeasesExpanded(v => !v)}
                  aria-expanded={leasesExpanded}
                  className="w-full flex items-center gap-2.5 text-left focus:outline-none group"
                >
                  <span aria-hidden="true" className="h-3.5 w-1 flex-none rounded-full" style={{ backgroundColor: alpha(CATEGORY.blue.ink, 0.6) }} />
                  <h3 className="m-0 text-[11px] font-semibold uppercase tracking-widest text-on-surface-variant" style={{ color: CATEGORY.blue.ink.oklch }}>
                    Leased Out <span className="font-normal normal-case tracking-normal text-on-surface-variant">({availability.leases.length})</span>
                  </h3>
                  <span className="ml-auto text-xs font-medium tabular-nums" style={{ color: CATEGORY.blue.ink.oklch }}>
                    {availability.totalLeased} drones
                  </span>
                  <svg
                    className={`w-3.5 h-3.5 text-on-surface-variant transition-transform duration-150 ${leasesExpanded ? 'rotate-90' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <Reveal show={leasesExpanded}>
                  <div className="space-y-1 pt-3">
                    {availability.leases.map((l, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-on-surface">{l.customer}</span>
                        <span className="text-on-surface-variant tabular-nums">
                          {l.ls > 0 && `${l.ls} LS`}{l.pyro > 0 && ` ${l.pyro}P`}
                          <span className="ml-1.5">{l.type === 'temporary' ? 'temp' : 'perm'}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </Panel>
            )}

            {/* Rentals on This Date */}
            {availability.rentals && availability.rentals.length > 0 && (
              <Panel
                on="panel"
                level={3}
                padding="sm"
                titleStyle="section"
                accent="orange"
                title={<>Rentals <span className="font-normal normal-case tracking-normal">({availability.rentals.length})</span></>}
                actions={<span className="text-xs font-medium tabular-nums" style={{ color: CATEGORY.orange.ink.oklch }}>{availability.committedByRentals} drones</span>}
              >
                <div className="space-y-1">
                  {availability.rentals.map((r, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-on-surface">{r.name}</span>
                      <span className="text-on-surface-variant tabular-nums font-mono">
                        {r.so_number}
                        <span className="ml-2">{r.ls > 0 && `${r.ls} LS`}{r.pyro > 0 && ` ${r.pyro}P`}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {/* Proximity Events */}
            {hasProximityEvents && (
              <Panel
                on="panel"
                level={3}
                padding="sm"
                title="Proximity Events"
                titleStyle="section"
                accent="primary"
                actions={pendingCount > 0 && (
                  <button
                    onClick={() => setShowPending(!showPending)}
                    className={`text-xs font-medium transition-colors duration-150 ${
                      showPending ? 'text-primary hover:text-primary/80' : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    {showPending ? 'Hide' : 'Show'} pending ({pendingCount})
                  </button>
                )}
              >
                {sameDayEvents.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-primary-ink">This Day</div>
                    {sameDayEvents.map(renderEvent)}
                  </div>
                )}

                {(beforeGroups.length > 0 || afterGroups.length > 0) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    {renderGroups(beforeGroups, 'No events before')}
                    {renderGroups(afterGroups, 'No events after')}
                  </div>
                )}
              </Panel>
            )}
          </>
        ) : (
          <EmptyState variant="area">No availability data</EmptyState>
        )}
        </FadeIn>
      </div>
    </Dialog>
  );
};

export default DateAvailabilityModal;
